import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import CourseCard from '../components/CourseCard';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';

const LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

export default function Courses() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [category, setCategory] = useState(params.get('category') || 'all');
  const [levels, setLevels] = useState([]);
  const [sort, setSort] = useState('popular');
  const [showFilter, setShowFilter] = useState(false);
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);

  useEffect(() => { api.categories().then(setCategories); }, []);
  useEffect(() => { api.courses().then(setCourses); }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (category !== 'all') p.set('category', category);
    if (q) p.set('q', q);
    setParams(p, { replace: true });
  }, [category, q, setParams]);

  const filtered = useMemo(() => {
    let list = courses;
    if (category !== 'all') list = list.filter(c => c.category === category);
    if (q) {
      const qq = q.toLowerCase();
      list = list.filter(c => c.title.toLowerCase().includes(qq) || (c.skills || []).some(s => s.toLowerCase().includes(qq)));
    }
    if (levels.length) list = list.filter(c => levels.includes(c.level));
    if (sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);
    else if (sort === 'price_low') list = [...list].sort((a, b) => a.discountPrice - b.discountPrice);
    else if (sort === 'price_high') list = [...list].sort((a, b) => b.discountPrice - a.discountPrice);
    else list = [...list].sort((a, b) => (b.students || 0) - (a.students || 0));
    return list;
  }, [q, category, levels, sort, courses]);

  const toggleLevel = (l) => setLevels(prev => prev.includes(l) ? prev.filter(x => x !== l) : [...prev, l]);

  return (
    <div className="pt-16 min-h-screen bg-gradient-to-b from-purple-50/40 to-white">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#6b47dc] via-[#7c3aed] to-[#a855f7] text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 relative">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-3">All courses</h1>
          <p className="text-lg opacity-90 mb-6 max-w-2xl">Explore live instructor-led courses designed by industry experts. Advance your career with hands-on projects.</p>
          <div className="relative max-w-xl">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by course name or skill" className="w-full pl-12 pr-4 py-3.5 rounded-xl text-gray-900 outline-none focus:ring-4 focus:ring-white/30 border-0 shadow-lg" />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 grid lg:grid-cols-[280px_1fr] gap-8">
        <aside className={`lg:sticky lg:top-24 lg:self-start ${showFilter ? 'fixed inset-0 z-40 bg-black/50 lg:bg-transparent lg:relative lg:inset-auto' : 'hidden lg:block'}`}>
          <div className={`bg-white rounded-2xl border border-gray-100 p-5 shadow-sm ${showFilter ? 'absolute inset-4 lg:relative lg:inset-auto overflow-auto' : ''}`}>
            <div className="flex items-center justify-between mb-4 lg:hidden">
              <h3 className="font-bold">Filters</h3>
              <button onClick={() => setShowFilter(false)}><X size={20} /></button>
            </div>
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">Category</h3>
            <div className="space-y-1 mb-6">
              <button onClick={() => setCategory('all')} className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${category === 'all' ? 'bg-[#6b47dc] text-white' : 'hover:bg-purple-50 text-gray-700'}`}>All Categories</button>
              {categories.map(c => (
                <button key={c.id} onClick={() => setCategory(c.id)} className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex justify-between ${category === c.id ? 'bg-[#6b47dc] text-white' : 'hover:bg-purple-50 text-gray-700'}`}>
                  <span>{c.name}</span><span className="text-xs opacity-60">{c.count}</span>
                </button>
              ))}
            </div>
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">Level</h3>
            <div className="space-y-2">
              {LEVELS.map(l => (
                <label key={l} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={levels.includes(l)} onChange={() => toggleLevel(l)} className="w-4 h-4 rounded accent-[#6b47dc]" />
                  <span className="text-sm text-gray-700">{l}</span>
                </label>
              ))}
            </div>
            {(levels.length || category !== 'all') ? (
              <Button variant="outline" size="sm" onClick={() => { setLevels([]); setCategory('all'); }} className="mt-5 w-full">Clear filters</Button>
            ) : null}
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <div><span className="font-bold text-lg text-gray-900">{filtered.length}</span><span className="text-gray-500"> courses found</span></div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowFilter(true)} className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold"><SlidersHorizontal size={14} /> Filters</button>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-semibold bg-white outline-none focus:border-[#6b47dc]">
                <option value="popular">Most popular</option>
                <option value="rating">Highest rated</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white rounded-2xl p-16 text-center border border-gray-100">
              <h3 className="font-bold text-lg mb-2">No courses match your filters</h3>
              <p className="text-gray-500 mb-4">Try adjusting your search or filters</p>
              <Button onClick={() => { setLevels([]); setCategory('all'); setQ(''); }}>Reset all</Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {filtered.map(c => <CourseCard key={c.id} course={c} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
